/**
 * The face page: Horton's face filling the page, the latest reply running as
 * a caption along the bottom. Paged against the transcript by
 * `HortonFacePager`, which owns the turning; this owns what is on the page.
 *
 * A tap anywhere on the face turns to the conversation — the pager swallows
 * the tap that ends a swipe, so a turn never doubles as one.
 *
 * The composer is not part of this on purpose. Whatever page is showing, the
 * way to talk to Horton stays where it is.
 */
import type { FaceState } from '../../presence/faces'
import HortonFace from '../HortonFace'
import { MarkdownMessage } from './MarkdownMessage'

export interface FacePageReply {
  content: string
  /** The engine's turn row id, when the reply can be read aloud. */
  turnId?: number
  error?: boolean
}

interface Props {
  face?: string
  state: FaceState
  /** False while the page is turned away: the face keeps its renderer and stops drawing. */
  showing: boolean
  latest: FacePageReply | null
  /** A reply is being read aloud right now. */
  speaking: boolean
  onTap: () => void
  onListen?: (turnId: number) => void
  onStop?: () => void
}

export function HortonFacePage({
  face, state, showing, latest, speaking, onTap, onListen, onStop,
}: Props) {
  const statusText =
    state === 'listening' ? 'listening…'
    : state === 'thinking' ? 'thinking…'
    : state === 'speaking' || speaking ? 'speaking'
    : null
  const replyText = latest && !latest.error && latest.content.trim() ? latest.content : null
  const canListen =
    !speaking && !latest?.error && typeof latest?.turnId === 'number' && Boolean(onListen)

  return (
    <div className="absolute inset-0 p-2 sm:p-3" data-testid="horton-face-page">
      <div
        // bg-stage is the renderers' own clear colour (theme.css), so a canvas
        // that has not loaded yet, or letterboxing, is invisible.
        className="relative h-full w-full rounded-2xl overflow-hidden bg-stage shadow-xl ring-1 ring-white/10 cursor-pointer select-none"
        onClick={onTap}
        data-testid="horton-face-card"
      >
        <HortonFace className="absolute inset-0" face={face} state={state} paused={!showing} />

        {/* Caption. This is the live region while the face is up: the
            transcript is inert then and the canvas is aria-hidden, so a
            screen reader hears the reply here or nowhere. */}
        <div
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent px-3 pb-3 pt-10 sm:px-4 sm:pb-4 text-white/90"
          aria-live={showing ? 'polite' : 'off'}
          aria-relevant="additions text"
        >
          {statusText && (
            <div className="mb-1.5 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5" role="status">
                <span
                  className={`h-1.5 w-1.5 rounded-full animate-pulse ${
                    state === 'thinking' ? 'bg-warn' : state === 'listening' ? 'bg-brand' : 'bg-ok'}`}
                  aria-hidden="true"
                />
                {statusText}
              </span>
              {speaking && onStop && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onStop() }}
                  className="underline decoration-dotted hover:text-white"
                  aria-label="Stop reading this reply"
                >
                  ■ stop
                </button>
              )}
            </div>
          )}

          {replyText ? (
            <div className="face-caption max-h-40 sm:max-h-48 overflow-y-auto text-sm leading-relaxed">
              <MarkdownMessage content={replyText} />
            </div>
          ) : latest?.error ? (
            <p className="text-sm">Something went wrong. Turn to the conversation to retry.</p>
          ) : !statusText ? (
            <p className="text-sm text-white/70">Say hello — type below, or tap the mic.</p>
          ) : null}

          <div className="mt-1.5 flex items-center gap-3 text-xs text-white/60">
            {canListen && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onListen!(latest!.turnId!) }}
                className="underline decoration-dotted hover:text-white"
                aria-label="Read this reply aloud"
              >
                ▶ listen
              </button>
            )}
            <span>tap, or swipe right, for the conversation</span>
          </div>
        </div>
      </div>
    </div>
  )
}
